import React, { useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, MessageSquareQuote, Pencil, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Skeleton } from '../../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { easerGet, easerPost, errorMessage, fmtDate, fmtTime } from '../../../lib/easerApi';
import { QcBody } from './qcShared';
import { QualityControlDialog } from './QualityControlDialog';

/**
 * Détail d'un contrôle qualité. L'Easer concerné peut répondre (commentaire interne) ; le manager peut modifier.
 * `controlId` ou `control` (déjà chargé). Marque le contrôle comme lu côté serveur à l'ouverture.
 */
export function QualityDetailDialog({ open, controlId, control: initial = null, userId, canEdit = false, onClose, onChanged, onUnauthorized }) {
  const [control, setControl] = useState(initial);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const id = controlId || initial?.id;
    if (!id) return undefined;
    let cancelled = false;
    setError(''); setReply('');
    setControl(initial);
    easerGet(`/api/easer/qualite/${id}`).then((r) => { if (!cancelled) { setControl(r.data.control); if (onChanged) onChanged(r.data.control); } })
      .catch((err) => {
        if (err?.response?.status === 401) return onUnauthorized();
        if (!cancelled) setError(errorMessage(err, 'Contrôle qualité indisponible'));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, controlId, initial?.id]);

  const concerned = control && userId && (control.easer_ids || []).includes(userId);

  const sendReply = async () => {
    if (!reply.trim()) return toast.error('Écrivez votre réponse');
    setSending(true);
    try {
      const res = await easerPost(`/api/easer/qualite/${control.id}/reponse`, { text: reply.trim() });
      setControl(res.data.control); setReply('');
      toast.success('Réponse envoyée au manager');
      if (onChanged) onChanged(res.data.control);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err, "Impossible d'envoyer la réponse"));
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md w-[95vw] max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="qc-detail-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-['Space_Grotesk'] flex items-center gap-2 pr-6"><ClipboardCheck className="h-5 w-5 text-[hsl(var(--primary))]" /> Contrôle qualité</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm">
              {control && (
                <>
                  <p className="font-medium text-foreground" data-testid="qc-detail-label">{control.label}{control.client_name ? ` · ${control.client_name}` : ''}</p>
                  <p>{control.event_date ? `${fmtDate(control.event_date, { weekday: 'short', day: 'numeric', month: 'short' })} ${fmtTime(control.event_date)}` : ''}{control.easer_names?.length ? ` · ${control.easer_names.join(', ')}` : ''}{control.fichinter_ref ? ` · ${control.fichinter_ref}` : ''}</p>
                </>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {!control && !error && <div className="space-y-3"><Skeleton className="h-14 rounded-xl" /><Skeleton className="h-40 rounded-xl" /></div>}
        {error && <p className="text-sm text-destructive" data-testid="qc-detail-error">{error}</p>}
        {control && (
          <div className="space-y-4">
            <QcBody control={control} />
            {canEdit && (
              <Button variant="outline" className="w-full h-11" onClick={() => setEditing(true)} data-testid="qc-detail-edit"><Pencil className="h-4 w-4 mr-1" /> Modifier le contrôle</Button>
            )}
            {concerned && !control.reply && (
              <div className="space-y-1.5 rounded-xl border border-border p-3" data-testid="qc-reply-form">
                <Label htmlFor="qc-reply" className="flex items-center gap-1"><MessageSquareQuote className="h-4 w-4" /> Votre réponse (visible du manager et de l'agence, jamais du client)</Label>
                <Textarea id="qc-reply" value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Votre point de vue, une précision, un engagement…" data-testid="qc-reply-input" />
                <Button onClick={sendReply} disabled={sending} className="w-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="qc-reply-submit">
                  {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Envoyer ma réponse
                </Button>
              </div>
            )}
          </div>
        )}
        {canEdit && control && (
          <QualityControlDialog open={editing} existing={control} onClose={() => setEditing(false)} onUnauthorized={onUnauthorized}
            onDone={(c) => { setEditing(false); setControl(c); if (onChanged) onChanged(c); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}
